import {
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Box,
    IconButton,
    Button,
    CircularProgress,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ActionButtons from '../../../components/TableView/ActionButtons';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import NotificationAddIcon from '@mui/icons-material/NotificationAdd';
import AlertDialog from '../../../components/AlertDialog/AlertDialog';
import type { AlertDialogFields } from '../../../components/AlertDialog/AlertDialog';
import { ClientAlertsProvider, useClientAlerts } from '../../../context/administration/ClientAlertsContext';
import SurfaceCard from '../../../components/Common/SurfaceCard';
import useCanAccess from '../../../hooks/useCanAccess';
import EmptyState from '../../../components/EmptyState';
import CircleNotificationsIcon from '@mui/icons-material/CircleNotifications';
import { DialogMode } from '../../../types/DialogTypes';

/*
    TODOs
    on mobile, there are 2 scrolls: fix that
    deleting an alert should not set loading state
*/

export function ClientAlerts() {
    const { t } = useTranslation("common");
    const { canAccessAction } = useCanAccess();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        alerts,
        alertsLoading: isLoading,
        handleDeleteAlert,
        handleMarkAlertAsResolved,
    } = useClientAlerts();

    const [searchText, setSearchText] = useState('');

    const [alertDialogInfo, setAlertDialogInfo] = useState<{
        isOpen: boolean;
        mode?: DialogMode;
        dialogData?: Partial<AlertDialogFields>;
    }>({ isOpen: false });

    const closeDialog = () => setAlertDialogInfo({ isOpen: false });

    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    }, []);

    const filteredAlerts = useMemo(() => {
        if (!searchText.trim()) return alerts;
        const lowerSearch = searchText.toLowerCase();
        return alerts.filter(alert =>
            alert.name?.toLowerCase().includes(lowerSearch) ||
            alert.targetcustomerlicenseplate?.toLowerCase().includes(lowerSearch));
    }, [alerts, searchText]);

    const searchField = useMemo(() => (
        <TextField
            variant="outlined"
            placeholder={t("search.filters.searchByLicensePlate")}
            value={searchText}
            onChange={handleSearchChange}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <SearchIcon />
                    </InputAdornment>
                ),
                inputProps: { sx: { pt: '8.5px', pb: '8.5px' } },
            }}
            style={{ width: '180px', borderRadius: '4px' }}
        />
    ), [t, searchText, handleSearchChange]);

    const createClientAlertButton = useMemo(() => {
        if (!canAccessAction('createAlert')) return null;
        return (
            <Button
                variant="outlined"
                startIcon={<NotificationAddIcon />}
                onClick={() => setAlertDialogInfo({ isOpen: true, mode: DialogMode.CREATE })}
                sx={{ whiteSpace: 'nowrap' }}
            >
                {t('createAlertDialog.addAlert')}
            </Button>
        )
    }, [t, canAccessAction]);

    const buildInitialValues = useCallback((alertId: number) => {
        if (!alertId) return {};
        const found = alerts.find(({ id }) => id === alertId);
        return {
            id: found?.id,
            numberPlate: found?.targetcustomerlicenseplate ?? '',
            title: found?.name ?? '',
            // Dialog expects 0/1 or boolean; server uses lowercase keys. Normalize to numbers used by Select controls.
            executeIfStaffInLocation: found?.executeonlyifstaffavailable,
            runOnlyOncePerCustomerOrStaff: found?.runonlyoncepercustomerorstaff,
            automaticallyMarkAsExecuted: found?.automaticallymarkasexecuted,
            adminMsg: found?.smstoadmins ?? '',
            ownerMsg: found?.smstoowners ?? '',
            message: found?.smstoavailablestaff ?? '',
            targetNewCustomers: found?.targetnewcustomers,
        };
    }, [alerts]);

    const getActionButtons = (id: number) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
            <IconButton size="small" aria-label="view" onClick={() => setAlertDialogInfo({ isOpen: true, mode: DialogMode.VIEW, dialogData: buildInitialValues(id) })}>
                <VisibilityIcon fontSize="small" />
            </IconButton>
            <ClientActionsMenu alertId={id} />
        </Box>
    );

    function ClientActionsMenu({ alertId }: { alertId: number }) {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const isOpen = Boolean(anchorEl);

        const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
        const handleClose = () => setAnchorEl(null);

        const handleEdit = () => {
            setAlertDialogInfo({ isOpen: true, mode: DialogMode.EDIT, dialogData: buildInitialValues(alertId) });
        };

        const buttons = [];
        if (canAccessAction('updateAlert')) {
            buttons.push(
                {
                    key: 'edit',
                    icon: <EditIcon fontSize='small' />,
                    label: t('common.edit'),
                    onClick: handleEdit,
                },
                {
                    key: 'mark-resolved',
                    icon: <DoneIcon fontSize='small' />,
                    label: t('administration.clientAlerts.markAsResolved'),
                    onClick: () => handleMarkAlertAsResolved(alertId),
                }
            );
        }
        if (canAccessAction('deleteAlert')) {
            buttons.push({
                key: 'delete',
                icon: <DeleteIcon fontSize='small' />,
                label: t('common.delete'),
                onClick: () => handleDeleteAlert(alertId),
            });
        }

        if (!buttons.length) return null;

        return (
            <>
                <IconButton size="small" onClick={handleOpen}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
                <ActionButtons
                    isPopoverOpen={isOpen}
                    anchorEl={anchorEl}
                    onPopoverClose={handleClose}
                    buttons={buttons}
                />
            </>
        );
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <SurfaceCard>
            <Box
                alignItems="center"
                display="flex"
                gap={2}
                justifyContent="space-between"
                width="100%"
            >
                {searchField}
                {createClientAlertButton}
            </Box>
            {filteredAlerts.length === 0 ? (
                <EmptyState
                    title={searchText.trim() ? t('administration.clientAlerts.no-search-results') : t('administration.clientAlerts.no-results')}
                    showParkingAnimation
                    icon={<CircleNotificationsIcon sx={{ fontSize: 60 }} />}
                />
            ) : (
                <TableContainer sx={{ maxHeight: '100%' }}>
                    <Table stickyHeader sx={{ tableLayout: { xs: 'fixed', sm: 'auto' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('administration.clientAlerts.create-date')}</TableCell>
                                <TableCell>{t(`administration.clientAlerts.${isMobile ? 'license-plate.short' : 'license-plate'}`)}</TableCell>
                                <TableCell>{t('administration.clientAlerts.title')}</TableCell>
                                <TableCell align="right">{t('administration.clientAlerts.results', { nr: filteredAlerts.length })}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredAlerts.map((alert, index: number) => {
                                const { id, createdatString } = alert;
                                // compute display name according to BE message:
                                // 1) use targetCustomerLicensePlate, targetGroupName or targetStaffName (first defined)
                                // 2) if none defined and targetnewcustomers is true -> 'Clienti noi'
                                // 3) otherwise empty string
                                const computeDisplayTarget = (a: any) => {
                                    // backend fields are lowercased in the client type
                                    const plate = a.targetcustomerlicenseplate;
                                    const group = a.targetgroupname;
                                    const staff = a.targetstaffname;
                                    if (plate) return plate;
                                    if (group) return group;
                                    if (staff) return staff;
                                    if (a.targetnewcustomers) return 'Clienti noi';
                                    return '';
                                };
                                const displayTarget = computeDisplayTarget(alert);
                                const name = (alert as any).name;
                                return (
                                    <TableRow
                                        hover
                                        key={id}
                                        sx={{
                                            backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.05)' : 'inherit',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                                            },
                                        }}
                                    >
                                        {/* todo actual create data */}
                                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'hidden' }}>{createdatString}</TableCell>
                                        {/* todo Radu will add more columns in BE */}
                                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'hidden' }}>{displayTarget}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'hidden' }}>{name}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'hidden' }}>
                                            {getActionButtons(id)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {alertDialogInfo.isOpen && (
                <AlertDialog
                    open={alertDialogInfo.isOpen}
                    mode={alertDialogInfo.mode ?? DialogMode.VIEW}
                    initialValues={alertDialogInfo.dialogData}
                    onClose={closeDialog}
                />
            )}
        </SurfaceCard>
    );
}

export default function ClientAlertsPage() {
    return (
        <ClientAlertsProvider>
            <ClientAlerts />
        </ClientAlertsProvider>
    );
}
